import React, { useEffect, useState } from 'react';
import Button from '../Button';
import Form, { useForm } from '../Form';
import Field from '../Field';
import iconAuto from '../../assets/icon-auto.svg';
import cardIcon from '../../assets/feeda-icon.svg';
import agentService from '../../services/agentService';
import Toast from '../Toast';
import { AuthContext } from '../../Context/authContext';
import { useContextHook } from 'use-context-hook';
import CenterModal from '../Modal/CenterModal';
import { FaWandMagicSparkles } from 'react-icons/fa6';
import { BsPlusSquare } from 'react-icons/bs';
import { AiOutlineCloseCircle } from 'react-icons/ai';
import { StyledCreatTeamModal } from './CreatTeamModal.style';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import teamService from '../../services/teamService';
import toolsService from '../../services/toolsService';

const teamModeOptions = [
  {
    label: (
      <>
        <strong>Route Mode:</strong>{' '}
        <span style={{ color: '#8d8d8d' }}>Tasks are automatically routed to the most appropriate agent.</span>
      </>
    ),
    value: 'route_mode',
  },
  {
    label: (
      <>
        <strong>Coordinate Mode:</strong>{' '}
        <span style={{ color: '#8d8d8d' }}>Agents take turns, building on each other&apos;s work sequentially.</span>
      </>
    ),
    value: 'coordinate_mode',
  },
  {
    label: (
      <>
        <strong>Collaborate Mode:</strong>{' '}
        <span style={{ color: '#8d8d8d' }}>
          All agents work on the same task simultaneously and discuss to reach a consensus
        </span>
      </>
    ),
    value: 'collaborate_mode',
  },
];

const CreatTeamModal = ({ onClose, setSuccessModal, refetch, isEdit, isDuplicate }) => {
  const [domainLoading, setDomainLoading] = useState(false);
  const [domainModal, setDomainModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promptLoading, setPromptLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [domains, setDomains] = useState([]);
  const [models, setModels] = useState([]);
  const [allModels, setAllModels] = useState([]);
  const [modelsKeys, setModelsKeys] = useState([]);
  const [activeModelsKeys, setActiveModelsKeys] = useState('OpenAi');
  const [tools, setTools] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentsData, setAgentsData] = useState(null);
  const [selectedTeamIcon, setSelectedTeamIcon] = useState(null);
  const [teamMembers, setTeamMembers] = useState([{ id: Date.now() }]);

  function base64ToFile(base64String, filename) {
    const [header, base64Data] = base64String.split(',');
    const mimeMatch = header.match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const binary = atob(base64Data);
    const length = binary.length;
    const u8arr = new Uint8Array(length);

    for (let i = 0; i < length; i++) {
      u8arr[i] = binary.charCodeAt(i);
    }

    return new File([u8arr], filename, { type: mime });
  }

  const findProvider = modelId => {
    const providerObj = models.find(obj => {
      const key = Object.keys(obj)[0];
      return obj[key].some(model => model.value === modelId);
    });

    return providerObj ? Object.keys(providerObj)[0] : null;
  };

  const togglePrompt = () => {
    setOpen(prev => !prev);
  };

  const toggleDomainModal = () => {
    setDomainModal(prev => !prev);
  };

  const getDomains = async () => {
    const resp = await agentService.getAllfeedaAgentsCategory();
    const restructuredDataForSelect = resp?.domains?.map(elem => ({ label: elem?.domain, value: elem?.domain_id }));
    setDomains([...restructuredDataForSelect]);
  };

  const getAllAgentData = useQuery({
    queryKey: ['get-all-agents'],
    queryFn: () => agentService.getAllAgent(),
    staleTime: Infinity,
    gcTime: Infinity,
  });
  useEffect(() => {
    if (getAllAgentData.isSuccess) {
      setAgentsData(
        getAllAgentData?.data?.agents?.map(data => ({
          label: data?.agent_name,
          value: data?.id,
        })),
      );
    }
  }, [getAllAgentData.isSuccess]);

  const getModels = async () => {
    try {
      const resp = await agentService.getAllModels();
      const allModels = resp?.models?.flatMap(providerObj => {
        const providerName = Object.keys(providerObj)[0];
        const models = providerObj[providerName];
        return models.map(model => ({
          ...model,
          provider: providerName,
        }));
      });

      const convertedData2 = allModels?.map(elem => ({
        label: (
          <div className="flex-wrap">
            <img src={elem?.icon} alt="img" width={30} height={30} />
            <span className="text">{elem?.name}</span>
          </div>
        ),
        value: elem?.id,
      }));

      const convertedData = resp?.models?.map(providerObj => {
        const providerName = Object.keys(providerObj)[0];
        const models = providerObj[providerName];

        const formattedModels = models.map(model => ({
          label: (
            <div className="flex-wrap">
              <img src={model?.icon} alt="img" width={30} height={30} />
              <span className="text">{model?.name}</span>
              <span className="description">{model?.description}</span>
            </div>
          ),
          value: model?.id,
          description: model?.description,
        }));

        // Return in the same structure
        return {
          [providerName]: formattedModels,
        };
      });

      const providerKeys = resp?.models?.map(providerObj => {
        const providerName = Object.keys(providerObj)[0];
        return {
          label: providerName,
          value: providerName,
        };
      });

      setModels(convertedData);
      setModelsKeys(providerKeys);
      setAllModels(convertedData2);
    } catch (error) {
      console.log(error);
    }
  };

  const getTools = async () => {
    try {
      const resp = await toolsService.getAllTools('user_created');
      const resp2 = await toolsService.getAllTools('admin_created');
      const convertedData = resp.tools.map(elem => ({
        label: (
          <>
            <div className="flex-wrap">
              <span className="text">{elem?.tool_name}</span>
            </div>
          </>
        ),
        value: elem?.tool_id,
      }));
      const convertedData2 = resp2.tools.map(elem => ({
        label: (
          <>
            <div className="flex-wrap tag-section">
              <span className="text">{elem?.tool_name}</span>
              <span className="tag-text">Admin Tools</span>
            </div>
          </>
        ),
        value: elem?.tool_id,
      }));
      setTools([...convertedData, ...convertedData2]);
    } catch (error) {
      console.log(error);
    }
  };

  const getTeam = async () => {
    try {
      const resp = await teamService.getTeamDetails(isEdit?.team_id);
      setSelectedAgent(resp?.detail);
      if (!isDuplicate) {
        setSelectedTeamIcon(
          resp?.detail?.icon === null || !resp?.detail?.icon ? cardIcon : base64ToFile(resp?.detail?.icon, 'icon.png'),
        );
      }
      setTeamMembers(
        resp?.detail?.members?.map((data, index) => ({
          id: index,
        })),
      );
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getModels();
    getTools();
    getDomains();
    if (isEdit) {
      getTeam();
    }
  }, []);

  useEffect(() => {
    if (isEdit && models.length !== 0 && selectedAgent !== null) {
      setSelectedTeamIcon(null);
      setTimeout(() => {
        form.setFieldsValue({
          team_name: selectedAgent?.team_name,
          description: selectedAgent?.description,
          domain_id: domains?.find(elem => elem?.value == selectedAgent?.domain_id),
          model_id: allModels?.find(elem => elem?.value == selectedAgent?.model_id),
          tool_id: tools?.find(elem => selectedAgent?.tools?.includes(elem?.value)),
          instructions: selectedAgent?.instructions,
        });
        setActiveModelsKeys(modelsKeys?.find(elem => elem?.value == findProvider(selectedAgent?.model_id)));

        if (!isDuplicate) {
          setSelectedTeamIcon(
            selectedAgent?.icon === null || !selectedAgent?.icon
              ? cardIcon
              : base64ToFile(selectedAgent?.icon, 'icon.png'),
          );
        }

        selectedAgent?.members?.forEach((data, index) => {
          form.setFieldsValue({
            [`select_agent_${index}`]: agentsData?.find(elem => elem?.value == data?.agent_id),
            [`assign_task_${index}`]: data?.agent_role,
          });
        });

        if (selectedAgent?.respond_directly === true) {
          form.setFieldsValue({
            team_mode: teamModeOptions?.find(elem => elem?.value === 'route_mode'),
          });
        } else if (selectedAgent?.determine_input_for_members === true) {
          form.setFieldsValue({
            team_mode: teamModeOptions?.find(elem => elem?.value === 'coordinate_mode'),
          });
        } else if (selectedAgent?.delegate_task_to_all_members === true) {
          form.setFieldsValue({
            team_mode: teamModeOptions?.find(elem => elem?.value === 'collaborate_mode'),
          });
        }
      }, 500);
    }
  }, [models, selectedAgent]);

  const convertFileToBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };

  const queryClient = useQueryClient();

  const handleSubmit = async e => {
    setLoading(true);

    if (isEdit && !isDuplicate) {
      editTeam.mutate(e);
    } else {
      addTeam.mutate(e);
    }
  };

  const editTeam = useMutation({
    mutationFn: async e => {
      const base64 = await convertFileToBase64(e?.icon ?? selectedTeamIcon);
      const payload = {
        team_id: isEdit?.team_id,
        team_name: e?.team_name,
        model_id: e?.model_id?.value,
        domain_id: e?.domain_id?.value,
        description: e?.description,
        instructions: e?.instructions,
        respond_directly: true,
        delegate_ask_to_all_members: false,
        determine_input_for_members: false,
        icon: base64,
        tools: e?.tool_id
          ? e?.tool_id?.map(data => {
              return data?.value;
            })
          : [],
        // respond_directly: e?.team_mode?.value === 'route_mode',
        // delegate_ask_to_all_members: e?.team_mode?.value === 'collaborate_mode',
        // determine_input_for_members: e?.team_mode?.value === 'coordinate_mode',
        members: Object.keys(e)
          .filter(key => key.startsWith('select_agent_'))
          .map(key => {
            const index = key.split('_').pop();
            return {
              agent_id: e[key]?.value,
              agent_name: e[key]?.label,
              agent_role: e[`assign_task_${index}`],
            };
          })
          .filter(member => member.agent_id && member.agent_name && member.agent_role),
      };

      return await teamService.updateTeam(payload);
    },
    onSuccess: response => {
      setLoading(false);
      refetch();
      onClose();
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: err => {
      setLoading(false);
      console.log(err.message);
      Toast({ type: 'error', message: err.message });
    },
  });

  const addTeam = useMutation({
    mutationFn: async e => {
      const base64 = await convertFileToBase64(e?.icon ?? selectedTeamIcon);
      const payload = {
        team_name: e?.team_name,
        model_id: e?.model_id?.value,
        domain_id: e?.domain_id?.value,
        description: e?.description,
        instructions: e?.instructions,
        respond_directly: true,
        delegate_ask_to_all_members: false,
        determine_input_for_members: false,
        icon: base64,
        tools: e?.tool_id
          ? e?.tool_id?.map(data => {
              return data?.value;
            })
          : [],
        // respond_directly: e?.team_mode?.value === 'route_mode',
        // delegate_ask_to_all_members: e?.team_mode?.value === 'collaborate_mode',
        // determine_input_for_members: e?.team_mode?.value === 'coordinate_mode',
        members: Object.keys(e)
          .filter(key => key.startsWith('select_agent_'))
          .map(key => {
            const index = key.split('_').pop();
            return {
              agent_id: e[key]?.value,
              agent_name: e[key]?.label,
              agent_role: e[`assign_task_${index}`],
            };
          })
          .filter(member => member.agent_id && member.agent_name && member.agent_role),
      };

      return await teamService.createTeam(payload);
    },
    onSuccess: response => {
      setLoading(false);
      refetch();
      onClose();
      setSuccessModal(true);
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: err => {
      setLoading(false);
      console.log(err.message);
      Toast({ type: 'error', message: err.message });
    },
  });

  const [form, form2, form3] = useForm();
  const generatePromt = async ({ prompt }) => {
    try {
      setPromptLoading(true);
      const resp = await agentService.generatePrompt(prompt);
      if (resp.success) {
        const formatted = resp.prompt.replace(/\\n/g, '\n');
        form.setFieldsValue({
          instructions: formatted,
        });
        Toast({
          type: 'success',
          message: 'Prompt Generated Successfully',
        });
        setPromptLoading(false);
        setOpen(false);
      }
    } catch (error) {
      console.log(error);
      setPromptLoading(false);
    }
  };

  const generateDomain = async e => {
    const payload = {
      domain: e?.domainName,
      description: e?.domainDescription,
    };
    try {
      setDomainLoading(true);
      const resp = await agentService.generateDomain(payload);
      if (resp.success) {
        Toast({
          type: 'success',
          message: 'Domain Created Successfully',
        });
        setDomainLoading(false);
        setDomainModal(false);
        getDomains();
      }
    } catch (error) {
      console.log(error);
      Toast({
        type: 'error',
        message: error || 'Something went wrong!!!',
      });
      setDomainLoading(false);
    }
  };

  const addMember = () => {
    setTeamMembers(prev => [...prev, { id: Date.now() }]);
  };

  const removeMember = id => {
    setTeamMembers(prev => prev.filter(m => m.id !== id));
  };

  return (
    <StyledCreatTeamModal>
      <CenterModal open={domainModal} setOpen={setDomainModal} width={'600'} title={'Create Custom Domain'}>
        <Form form={form3} className="prompt-holder" onSubmit={generateDomain}>
          <Form.Item label="Domain Name" type="text" name="domainName" rounded placeholder="Enter your domain name">
            <Field />
          </Form.Item>
          <Form.Item
            label="Description"
            type="textarea"
            name="domainDescription"
            rounded
            placeholder="Enter your description here.">
            <Field />
          </Form.Item>
          <Button width="200px" loader={domainLoading}>
            Create Domain
          </Button>
        </Form>
      </CenterModal>
      <CenterModal open={open} setOpen={setOpen} width={'600'} title={'Generate team instructions'}>
        <Form form={form2} className="prompt-holder" onSubmit={generatePromt}>
          <Form.Item label="" type="textarea" name="prompt" rounded placeholder="Enter your prompt here.">
            <Field />
          </Form.Item>
          <Button width="200px" loader={promptLoading}>
            Generate prompt
          </Button>
        </Form>
      </CenterModal>
      <Form form={form} onSubmit={handleSubmit}>
        <div className="column-wrap">
          <div className="column">
            <Form.Item
              label="Team Name"
              type="text"
              rounded
              name="team_name"
              placeholder="Name your team"
              rules={[
                {
                  required: true,
                  message: 'Team name is required',
                },
              ]}>
              <Field />
            </Form.Item>
            <Form.Item
              label="Icon"
              type="img"
              rounded
              name="icon"
              uploadTitle="Upload Icon"
              disc="File size must be less than 5mb in JPG, PNG or SVG format."
              img={selectedTeamIcon}
              rules={[
                {
                  required: isEdit ? false : true,
                  message: 'Icon is required',
                },
              ]}>
              <Field />
            </Form.Item>
            <Form.Item
              options={domains}
              label={
                <>
                  Domain
                  <span className="btn-domain" onClick={toggleDomainModal}>
                    Create Domain <BsPlusSquare />
                  </span>
                </>
              }
              type="select"
              rounded
              name="domain_id"
              placeholder="Select any domain">
              <Field />
            </Form.Item>
            <Form.Item
              options={modelsKeys}
              label="Provider"
              type="select"
              rounded
              name="model_provider"
              placeholder="Select any provider to proceed"
              value={activeModelsKeys}
              onChange={e => {
                setActiveModelsKeys(e.target?.value);
              }}>
              <Field />
            </Form.Item>
            {/* <div className="item-holder">
                         <strong className="title">Response Model</strong> */}
            <Form.Item
              options={models?.find(obj => Object.keys(obj)[0] === activeModelsKeys?.value)?.[activeModelsKeys?.value]}
              label="Response Model"
              type="select"
              rounded
              name="model_id"
              placeholder="Select any model to proceed"
              // placeholder={
              //   <>
              //     <div className="flex-wrap">
              //       <img src={iconAuto} alt="img" />
              //       <span className="text">Auto</span>
              //     </div>
              //   </>
              // }
              rules={[
                {
                  required: true,
                  message: 'response model is required',
                },
              ]}>
              <Field />
            </Form.Item>
            <div className="item-holder">
              <Form.Item
                options={tools}
                label="Tools"
                type="select"
                rounded
                name="tool_id"
                placeholder="Select Tool"
                isMulti>
                <Field />
              </Form.Item>
            </div>
            <div className="team-members">
              <div className="team-header">
                <span className="title">Team Members</span>
                <button type="button" className="btn-add" onClick={addMember}>
                  <BsPlusSquare />
                </button>
              </div>
              {teamMembers.map((member, index) => (
                <div className="input-wrap" key={member.id}>
                  <span className="btn-close" onClick={() => removeMember(member.id)}>
                    <AiOutlineCloseCircle />
                  </span>
                  <div className="input-box">
                    <Form.Item
                      type="select"
                      rounded
                      name={`select_agent_${index}`}
                      placeholder="Select Agent"
                      options={agentsData}>
                      <Field />
                    </Form.Item>
                  </div>
                  <div className="input-box">
                    <Form.Item type="text" rounded name={`assign_task_${index}`} placeholder="Assign task">
                      <Field />
                    </Form.Item>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="column">
            <Form.Item
              customHeight
              noMargin
              label={
                <>
                  Instructions
                  <span className="btn-prompt" onClick={togglePrompt}>
                    <FaWandMagicSparkles />
                  </span>
                </>
              }
              type="textarea"
              rounded
              name="instructions"
              placeholder="Enter your prompt here. Be creative, be bold, or just be straightforward. We’ll handle it either way!"
              rules={[
                {
                  required: true,
                  message: 'Instructions is required',
                },
              ]}>
              <Field />
            </Form.Item>
          </div>
        </div>
        <Form.Item
          label="Description"
          type="textarea"
          rounded
          name="description"
          placeholder="This project is a collection of threads related to planning..."
          rules={[
            {
              required: true,
              message: 'description is required',
            },
          ]}>
          <Field />
        </Form.Item>
        <div className="btn-holder">
          <Button width="150px" variant="secondary-outline" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button width="150px" loader={loading}>
            {isEdit && !isDuplicate ? 'Update Team' : 'Create Team'}
          </Button>
        </div>
      </Form>
    </StyledCreatTeamModal>
  );
};

export default CreatTeamModal;
